import { ChevronDown, Tag } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { PortalDropdown } from '../../components/PortalDropdown';

interface StatusOption {
  value: string
  label: string
}

interface SelectFilterProps {
  title: string
  placeholder?: string
  selectionOptions: StatusOption[]
  activeSelections: string[]
  onSelectionsChange: (statuses: string[]) => void
  searchActive?: boolean
  icon?: React.ReactNode
  className?: string
  usePortal?: boolean // New prop to enable portal rendering
}

export const SelectFilter = ({
  selectionOptions,
  activeSelections,
  onSelectionsChange,
  title,
  placeholder = 'Buscar...',
  searchActive = true,
  icon,
  className,
  usePortal = false,
}: SelectFilterProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const toggleStatus = (value: string) => {
    if (value === 'all') {
      onSelectionsChange(['all'])
      return
    }

    // Remove "all" if it's in the array
    let newStatuses = activeSelections.filter((s) => s !== 'all')

    // Toggle the selected status
    if (newStatuses.includes(value)) {
      newStatuses = newStatuses.filter((s) => s !== value)
      // If no statuses left, set back to "all"
      if (newStatuses.length === 0) {
        newStatuses = ['all']
      }
    } else {
      newStatuses.push(value)
    }

    onSelectionsChange(newStatuses)
  }

  const getActiveCount = () => {
    if (activeSelections.includes('all')) return 0
    return activeSelections.length
  }

  const activeCount = getActiveCount()

  const triggerButton = (
    <Button
      variant="outline"
      className={cn('gap-1.5', className)}
      aria-label={title}
      onClick={() => usePortal && setIsOpen(!isOpen)}
    >
      {icon ? icon : <Tag className="h-4 w-4" />}
      <span>{title}</span>
      <ChevronDown className="h-4 w-4 opacity-50" />
      {activeCount > 0 && (
        <Badge variant="secondary" className="ml-1 rounded-full px-1.5">
          {activeCount}
        </Badge>
      )}
    </Button>
  )

  const dropdownContent = (
    <Command>
      {searchActive && <CommandInput placeholder={placeholder} />}
      <CommandList>
        <CommandEmpty>No se encontraron resultados.</CommandEmpty>
        <CommandGroup>
          {selectionOptions.map((status) => (
            <CommandItem
              key={status.value}
              onSelect={() => toggleStatus(status.value)}
              className="flex cursor-pointer items-center gap-2"
            >
              <div className="flex flex-1 items-center gap-2">
                <Checkbox
                  checked={activeSelections.includes(status.value)}
                  onCheckedChange={() => toggleStatus(status.value)}
                />
                <span>{status.label}</span>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  )

  if (usePortal) {
    return (
      <PortalDropdown
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        trigger={triggerButton}
        className="w-[200px] p-0"
      >
        {dropdownContent}
      </PortalDropdown>
    )
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        {triggerButton}
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        {dropdownContent}
      </PopoverContent>
    </Popover>
  )
}
